import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useJournal } from './JournalContext';
import { useVoice } from './VoiceContext';
import crisisResourcesData from '../data/crisisResources.json';

export interface CrisisDetection {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  triggers: string[];
  confidence: number;
  timestamp: Date;
  source: 'text' | 'voice' | 'behavioral' | 'manual';
  interventionRequired: boolean;
}

export interface SafetyPlan {
  id: string;
  warningSignsPersonal: string[];
  warningSignsExternal: string[];
  copingStrategies: string[];
  socialSupports: {
    name: string;
    relationship: string;
    phone: string;
    available24h: boolean;
  }[];
  professionalContacts: {
    name: string;
    type: 'therapist' | 'psychiatrist' | 'counselor' | 'crisis_center';
    phone: string;
    address?: string;
    available24h: boolean;
  }[];
  environmentalSafety: string[];
  reasonsToLive: string[];
  lastUpdated: Date;
}

export interface TherapistProfile {
  id: string;
  name: string;
  credentials: string[];
  licenseNumber: string;
  licenseState: string;
  specializations: string[];
  approaches: string[];
  languages: string[];
  availability: {
    timezone: string;
    schedule: Record<string, { start: string; end: string }[]>;
    emergencyAvailable: boolean;
  };
  rating: number;
  reviewCount: number;
  verified: boolean;
  insuranceAccepted: string[];
  sessionTypes: ('video' | 'phone' | 'chat' | 'in_person')[];
  rates: {
    individual: number;
    couples: number;
    group: number;
  };
  bio: string;
  profileImage?: string;
}

export interface CrisisIntervention {
  id: string;
  userId: string;
  detectionId: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  interventionType: 'ai_support' | 'crisis_counselor' | 'emergency_services' | 'safety_plan';
  status: 'initiated' | 'in_progress' | 'resolved' | 'escalated';
  timeline: {
    timestamp: Date;
    action: string;
    performer: 'system' | 'ai' | 'counselor' | 'emergency' | 'user';
    notes?: string;
  }[];
  outcome: string;
  followUpRequired: boolean;
  followUpScheduled?: Date;
}

export interface CrisisResource {
  id: string;
  name: string;
  type: 'hotline' | 'text' | 'chat' | 'emergency' | 'local_center';
  phone?: string;
  textNumber?: string;
  website?: string;
  description: string;
  availability: '24/7' | 'business_hours' | 'limited';
  languages: string[];
  specializations: string[];
  location?: {
    country: string;
    state?: string;
    city?: string;
  };
}

interface CrisisContextType {
  // Crisis Detection
  detectCrisis: (content: string, source: 'text' | 'voice') => Promise<CrisisDetection | null>;
  monitorBehavioralPatterns: () => void;
  getCrisisHistory: () => CrisisDetection[];
  
  // Crisis Response
  activateCrisisProtocol: (detection: CrisisDetection) => Promise<CrisisIntervention>;
  escalateToEmergency: (interventionId: string) => Promise<void>;
  connectToCrisisCounselor: () => Promise<void>;
  
  // Safety Planning
  createSafetyPlan: (plan: Partial<SafetyPlan>) => Promise<SafetyPlan>;
  updateSafetyPlan: (id: string, updates: Partial<SafetyPlan>) => Promise<void>;
  getSafetyPlan: () => SafetyPlan | null;
  activateSafetyPlan: () => Promise<void>;
  
  // Professional Network
  searchTherapists: (criteria: any) => Promise<TherapistProfile[]>;
  bookConsultation: (therapistId: string, datetime: Date) => Promise<string>;
  shareJournalWithTherapist: (therapistId: string, entryIds: string[]) => Promise<void>;
  
  // Crisis Resources
  getCrisisResources: (location?: string) => CrisisResource[];
  getEmergencyContacts: () => any[];
  
  // Follow-up & Monitoring
  scheduleFollowUp: (interventionId: string, datetime: Date) => Promise<void>;
  trackRecoveryProgress: () => any;
  
  // State
  currentCrisisLevel: 'none' | 'low' | 'medium' | 'high' | 'critical';
  activeCrisis: CrisisIntervention | null;
  safetyPlan: SafetyPlan | null;
  isMonitoring: boolean;
}

const CrisisContext = createContext<CrisisContextType | undefined>(undefined);

export const useCrisis = () => {
  const context = useContext(CrisisContext);
  if (context === undefined) {
    throw new Error('useCrisis must be used within a CrisisProvider');
  }
  return context;
};

// Import crisis detection keywords and patterns from external JSON
const CRISIS_KEYWORDS = crisisResourcesData.crisisKeywords;
const BEHAVIORAL_PATTERNS = crisisResourcesData.behavioralPatterns;

// Crisis resources database
const CRISIS_RESOURCES: CrisisResource[] = crisisResourcesData.resources;

export const CrisisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { entries } = useJournal();
  const { speak } = useVoice();
  
  const [currentCrisisLevel, setCurrentCrisisLevel] = useState<'none' | 'low' | 'medium' | 'high' | 'critical'>('none');
  const [activeCrisis, setActiveCrisis] = useState<CrisisIntervention | null>(null);
  const [safetyPlan, setSafetyPlan] = useState<SafetyPlan | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [crisisHistory, setCrisisHistory] = useState<CrisisDetection[]>([]);
  
  const monitoringInterval = useRef<NodeJS.Timeout>();

  // Initialize crisis monitoring
  useEffect(() => {
    if (user) {
      loadSafetyPlan();
      startBehavioralMonitoring();
    }
    
    return () => {
      if (monitoringInterval.current) {
        clearInterval(monitoringInterval.current);
      }
    };
  }, [user]);

  const loadSafetyPlan = () => {
    const stored = localStorage.getItem(`safety_plan_${user?.id}`);
    if (stored) {
      const plan = JSON.parse(stored);
      setSafetyPlan({
        ...plan,
        lastUpdated: new Date(plan.lastUpdated)
      });
    }
  };

  const startBehavioralMonitoring = () => {
    setIsMonitoring(true);
    
    // Monitor every 5 minutes
    monitoringInterval.current = setInterval(() => {
      monitorBehavioralPatterns();
    }, 5 * 60 * 1000);
  };

  const detectCrisis = async (content: string, source: 'text' | 'voice'): Promise<CrisisDetection | null> => {
    const lowerContent = content.toLowerCase();
    let severity: 'low' | 'medium' | 'high' | 'critical' = 'low';
    let triggers: string[] = [];
    let confidence = 0;

    // Check for critical keywords
    const criticalMatches = CRISIS_KEYWORDS.critical.filter(keyword => lowerContent.includes(keyword));
    if (criticalMatches.length > 0) {
      severity = 'critical';
      triggers.push(...criticalMatches);
      confidence = 0.95;
    }
    
    // Check for high-risk keywords
    const highMatches = CRISIS_KEYWORDS.high.filter(keyword => lowerContent.includes(keyword));
    if (highMatches.length > 0 && severity !== 'critical') {
      severity = 'high';
      triggers.push(...highMatches);
      confidence = 0.8;
    }
    
    // Check for medium-risk keywords
    const mediumMatches = CRISIS_KEYWORDS.medium.filter(keyword => lowerContent.includes(keyword));
    if (mediumMatches.length > 2 && severity === 'low') {
      severity = 'medium';
      triggers.push(...mediumMatches);
      confidence = 0.6;
    }

    // Behavioral pattern analysis
    Object.entries(BEHAVIORAL_PATTERNS).forEach(([pattern, keywords]) => {
      const matches = keywords.filter(keyword => lowerContent.includes(keyword));
      if (matches.length > 0) {
        triggers.push(`behavioral_${pattern}`);
        confidence += 0.1;
      }
    });

    if (triggers.length === 0) return null;

    const detection: CrisisDetection = {
      id: `crisis_${Date.now()}`,
      severity,
      triggers,
      confidence: Math.min(confidence, 1),
      timestamp: new Date(),
      source,
      interventionRequired: severity === 'critical' || severity === 'high'
    };

    setCrisisHistory(prev => [detection, ...prev.slice(0, 49)]); // Keep last 50
    setCurrentCrisisLevel(severity);

    // Auto-activate crisis protocol for high/critical
    if (detection.interventionRequired) {
      await activateCrisisProtocol(detection);
    }

    return detection;
  };

  const monitorBehavioralPatterns = () => {
    if (entries.length < 3) return;

    const recentEntries = entries.slice(0, 7); // Last 7 entries
    const avgMood = recentEntries.reduce((sum, entry) => sum + entry.mood, 0) / recentEntries.length;
    const avgAnxiety = recentEntries.reduce((sum, entry) => sum + entry.anxiety, 0) / recentEntries.length;
    const avgSleep = recentEntries.reduce((sum, entry) => sum + entry.sleepQuality, 0) / recentEntries.length;

    let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
    const triggers: string[] = [];

    if (avgMood <= 3) {
      riskLevel = 'high';
      triggers.push('persistent_low_mood');
    }
    
    if (avgAnxiety >= 8) {
      riskLevel = riskLevel === 'high' ? 'critical' : 'high';
      triggers.push('severe_anxiety');
    }
    
    if (avgSleep <= 3) {
      triggers.push('sleep_disruption');
    }

    // Check for declining patterns
    const moodTrend = recentEntries.slice(0, 3).every((entry, index) => 
      index === 0 || entry.mood < recentEntries[index - 1].mood
    );
    
    if (moodTrend) {
      triggers.push('declining_mood_pattern');
      riskLevel = riskLevel === 'low' ? 'medium' : riskLevel;
    }

    if (triggers.length > 0) {
      const detection: CrisisDetection = {
        id: `behavioral_${Date.now()}`,
        severity: riskLevel,
        triggers,
        confidence: 0.7,
        timestamp: new Date(),
        source: 'behavioral',
        interventionRequired: riskLevel === 'high' || riskLevel === 'critical'
      };

      setCrisisHistory(prev => [detection, ...prev.slice(0, 49)]);
      setCurrentCrisisLevel(riskLevel);

      if (detection.interventionRequired) {
        activateCrisisProtocol(detection);
      }
    }
  };

  const activateCrisisProtocol = async (detection: CrisisDetection): Promise<CrisisIntervention> => {
    const intervention: CrisisIntervention = {
      id: `intervention_${Date.now()}`,
      userId: user?.id || '',
      detectionId: detection.id,
      severity: detection.severity,
      interventionType: detection.severity === 'critical' ? 'emergency_services' : 'ai_support',
      status: 'initiated',
      timeline: [{
        timestamp: new Date(),
        action: 'Crisis protocol activated',
        performer: 'system',
        notes: `Triggered by: ${detection.triggers.join(', ')}`
      }],
      outcome: '',
      followUpRequired: true
    };

    setActiveCrisis(intervention);

    // Immediate AI response
    if (detection.severity === 'critical') {
      await speak(
        `${user?.firstName}, I'm very concerned about you right now. You're not alone. I'm connecting you with immediate crisis support. Please stay with me.`,
        { emotion: 'concerned' }
      );
      
      // Auto-dial emergency services (in production)
      intervention.timeline.push({
        timestamp: new Date(),
        action: 'Emergency services contacted',
        performer: 'system'
      });
      
    } else if (detection.severity === 'high') {
      await speak(
        `${user?.firstName}, I can see you're going through something really difficult right now. Let's work through this together. Can you tell me what's happening?`
      );
      
      // Activate safety plan if available
      if (safetyPlan) {
        await activateSafetyPlan();
      }
    }

    // Save intervention
    const interventions = JSON.parse(localStorage.getItem(`crisis_interventions_${user?.id}`) || '[]');
    interventions.unshift(intervention);
    localStorage.setItem(`crisis_interventions_${user?.id}`, JSON.stringify(interventions));

    return intervention;
  };

  const escalateToEmergency = async (interventionId: string): Promise<void> => {
    if (!activeCrisis || activeCrisis.id !== interventionId) return;

    const updatedIntervention = {
      ...activeCrisis,
      interventionType: 'emergency_services' as const,
      status: 'escalated' as const,
      timeline: [
        ...activeCrisis.timeline,
        {
          timestamp: new Date(),
          action: 'Escalated to emergency services',
          performer: 'system' as const
        }
      ]
    };

    setActiveCrisis(updatedIntervention);
    
    // In production, this would contact emergency services
    await speak('I\'m contacting emergency services for you right now. Help is on the way.');
  };

  const connectToCrisisCounselor = async (): Promise<void> => {
    // Simulate connecting to crisis counselor
    await speak('I\'m connecting you with a trained crisis counselor. Please hold on.');
    
    if (activeCrisis) {
      const updatedIntervention = {
        ...activeCrisis,
        interventionType: 'crisis_counselor' as const,
        timeline: [
          ...activeCrisis.timeline,
          {
            timestamp: new Date(),
            action: 'Connected to crisis counselor',
            performer: 'system' as const
          }
        ]
      };
      setActiveCrisis(updatedIntervention);
    }
  };

  const createSafetyPlan = async (plan: Partial<SafetyPlan>): Promise<SafetyPlan> => {
    const newPlan: SafetyPlan = {
      id: `safety_plan_${Date.now()}`,
      warningSignsPersonal: plan.warningSignsPersonal || [],
      warningSignsExternal: plan.warningSignsExternal || [],
      copingStrategies: plan.copingStrategies || [],
      socialSupports: plan.socialSupports || [],
      professionalContacts: plan.professionalContacts || [],
      environmentalSafety: plan.environmentalSafety || [],
      reasonsToLive: plan.reasonsToLive || [],
      lastUpdated: new Date()
    };

    setSafetyPlan(newPlan);
    localStorage.setItem(`safety_plan_${user?.id}`, JSON.stringify(newPlan));
    
    return newPlan;
  };

  const updateSafetyPlan = async (id: string, updates: Partial<SafetyPlan>): Promise<void> => {
    if (!safetyPlan || safetyPlan.id !== id) return;

    const updatedPlan = {
      ...safetyPlan,
      ...updates,
      lastUpdated: new Date()
    };

    setSafetyPlan(updatedPlan);
    localStorage.setItem(`safety_plan_${user?.id}`, JSON.stringify(updatedPlan));
  };

  const activateSafetyPlan = async (): Promise<void> => {
    if (!safetyPlan) return;

    await speak(`${user?.firstName}, let's use your safety plan. Remember your coping strategies: ${safetyPlan.copingStrategies.slice(0, 2).join(' and ')}.`);
    
    if (activeCrisis) {
      const updatedIntervention = {
        ...activeCrisis,
        interventionType: 'safety_plan' as const,
        timeline: [
          ...activeCrisis.timeline,
          {
            timestamp: new Date(),
            action: 'Safety plan activated',
            performer: 'system' as const
          }
        ]
      };
      setActiveCrisis(updatedIntervention);
    }
  };

  const searchTherapists = async (criteria: any): Promise<TherapistProfile[]> => {
    // Simulate therapist search
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock therapist data
    return [
      {
        id: 'therapist_1',
        name: 'Dr. Sarah Johnson',
        credentials: ['PhD', 'Licensed Clinical Psychologist'],
        licenseNumber: 'PSY12345',
        licenseState: 'CA',
        specializations: ['Anxiety', 'Depression', 'Trauma', 'CBT'],
        approaches: ['Cognitive Behavioral Therapy', 'Mindfulness-Based Therapy'],
        languages: ['English', 'Spanish'],
        availability: {
          timezone: 'PST',
          schedule: {
            monday: [{ start: '09:00', end: '17:00' }],
            tuesday: [{ start: '09:00', end: '17:00' }],
            wednesday: [{ start: '09:00', end: '17:00' }],
            thursday: [{ start: '09:00', end: '17:00' }],
            friday: [{ start: '09:00', end: '15:00' }]
          },
          emergencyAvailable: true
        },
        rating: 4.8,
        reviewCount: 127,
        verified: true,
        insuranceAccepted: ['Blue Cross', 'Aetna', 'United Healthcare'],
        sessionTypes: ['video', 'phone', 'in_person'],
        rates: {
          individual: 150,
          couples: 200,
          group: 75
        },
        bio: 'Dr. Johnson specializes in anxiety and depression treatment with over 10 years of experience.'
      }
    ];
  };

  const bookConsultation = async (therapistId: string, datetime: Date): Promise<string> => {
    // Simulate booking
    await new Promise(resolve => setTimeout(resolve, 1000));
    return `booking_${Date.now()}`;
  };

  const shareJournalWithTherapist = async (therapistId: string, entryIds: string[]): Promise<void> => {
    // Simulate sharing with consent
    await new Promise(resolve => setTimeout(resolve, 500));
  };

  const getCrisisResources = (location?: string): CrisisResource[] => {
    if (location) {
      return CRISIS_RESOURCES.filter(resource => 
        resource.location?.country === location || 
        resource.location?.country === 'Global'
      );
    }
    return CRISIS_RESOURCES;
  };

  const getEmergencyContacts = () => {
    return safetyPlan?.socialSupports.filter(contact => contact.available24h) || [];
  };

  const scheduleFollowUp = async (interventionId: string, datetime: Date): Promise<void> => {
    if (activeCrisis && activeCrisis.id === interventionId) {
      const updatedIntervention = {
        ...activeCrisis,
        followUpScheduled: datetime,
        timeline: [
          ...activeCrisis.timeline,
          {
            timestamp: new Date(),
            action: `Follow-up scheduled for ${datetime.toLocaleString()}`,
            performer: 'system' as const
          }
        ]
      };
      setActiveCrisis(updatedIntervention);
    }
  };

  const trackRecoveryProgress = () => {
    const recentEntries = entries.slice(0, 7);
    const avgMood = recentEntries.reduce((sum, entry) => sum + entry.mood, 0) / recentEntries.length;
    
    return {
      moodTrend: avgMood,
      entriesCount: recentEntries.length,
      lastCrisis: crisisHistory[0]?.timestamp,
      recoveryDays: crisisHistory[0] ? 
        Math.floor((Date.now() - crisisHistory[0].timestamp.getTime()) / (1000 * 60 * 60 * 24)) : 0
    };
  };

  const getCrisisHistory = () => crisisHistory;

  const getSafetyPlan = () => safetyPlan;

  const value = {
    // Crisis Detection
    detectCrisis,
    monitorBehavioralPatterns,
    getCrisisHistory,
    
    // Crisis Response
    activateCrisisProtocol,
    escalateToEmergency,
    connectToCrisisCounselor,
    
    // Safety Planning
    createSafetyPlan,
    updateSafetyPlan,
    getSafetyPlan,
    activateSafetyPlan,
    
    // Professional Network
    searchTherapists,
    bookConsultation,
    shareJournalWithTherapist,
    
    // Crisis Resources
    getCrisisResources,
    getEmergencyContacts,
    
    // Follow-up & Monitoring
    scheduleFollowUp,
    trackRecoveryProgress,
    
    // State
    currentCrisisLevel,
    activeCrisis,
    safetyPlan,
    isMonitoring
  };

  return <CrisisContext.Provider value={value}>{children}</CrisisContext.Provider>;
};